import { useEffect, useState } from "react";
import { Input } from "antd";
import { useLoader } from "../../../../../utils/LoaderContext";
import { useAllOrders } from "../../../../../api/orders/orders";

const ORDERS_PER_PAGE = 20;

const Cancelled = ({ status = "cancelled" }) => {
  const { setLoading } = useLoader();

  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // ---------------------------
  // 🔹 Debounce Search Input
  // ---------------------------
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText.trim());
    }, 400);

    return () => clearTimeout(timer);
  }, [searchText]);

  // 🔹 Reset page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchText]);

  // ---------------------------
  // 🔹 Orders API (Single Source)
  // ---------------------------
  const {
    data: allOrders = { orders: [], totalPages: 1 },
    isLoading,
    isFetching,
  } = useAllOrders(
    {
      currentPage,
      limit: ORDERS_PER_PAGE,
      status,
      search: debouncedSearchText,
    },
    {
      keepPreviousData: true,
    }
  );

  const orders = allOrders.orders || [];
  const totalPages = allOrders.totalPages || 1;

  return (
    <div className="w-full mt-6">
      {/* ---------------- Search ---------------- */}
      <div className="mb-4 flex justify-between items-center">
        <Input
          className="w-80"
          placeholder="Search by AWB, Order Number or Name"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
        />
      </div>

      {/* ---------------- Table ---------------- */}
      <div className="overflow-x-auto hide-scrollbar">
        <table className="min-w-[1000px] w-full table-auto border border-gray-200">
          <thead className="bg-gray-100 text-left text-sm font-semibold text-gray-700">
            <tr>
              <th className="p-3">Order Date</th>
              <th className="p-3">Order Id / Status</th>
              <th className="p-3">Payment</th>
              <th className="p-3">Products</th>
              <th className="p-3">Customer</th>
              <th className="p-3">Address</th>
            </tr>
          </thead>

          <tbody className="bg-white text-sm">
            {isLoading ? (
              <tr>
                <td colSpan={6}>
                  <div className="flex justify-center items-center py-10 text-gray-500 space-x-2">
                    <span className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></span>
                    <span>Loading cancelled orders...</span>
                  </div>
                </td>
              </tr>
            ) : orders.length > 0 ? (
              orders.map((order, index) => (
                <tr key={order._id || index} className="hover:bg-gray-50">
                  <td className="p-4">
                    {new Date(order.createdAt).toLocaleDateString()}
                  </td>

                  <td className="p-4">
                    <div className="font-medium text-blue-600">
                      {order.OrderNumber}
                    </div>
                    <span className="inline-block mt-1 text-xs font-semibold bg-red-100 text-red-600 px-2 py-1 rounded">
                      {order.shippingStatusText}
                    </span>
                  </td>

                  <td className="p-4">
                    <p>₹{order.InvoiceAmount}</p>
                    <span className="inline-block mt-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                      {order.PaymentType}
                    </span>
                  </td>

                  <td className="p-4">
                    {order.ProductDetails?.map((prod, i) => (
                      <p key={i} className="truncate">
                        {prod.name}
                      </p>
                    ))}
                  </td>

                  <td className="p-4">
                    <p>{order.CustomerName}</p>
                    <p className="text-xs text-gray-500">
                      {order.BillingAddress?.Contact}
                    </p>
                  </td>

                  <td className="p-4 text-sm">
                    <p>{order.BillingAddress?.City}</p>
                    <p>{order.BillingAddress?.State}</p>
                    <p>{order.BillingAddress?.Pincode}</p>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="text-center py-6 text-gray-400">
                  No cancelled orders found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ---------------- Pagination ---------------- */}
      <div className="flex justify-between items-center mt-6 text-sm text-gray-700">
        <p>
          Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
          {isFetching && (
            <span className="ml-3 text-blue-500 animate-pulse">Loading...</span>
          )}
        </p>

        <div className="space-x-2">
          <button
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            disabled={currentPage === 1 || isFetching}
            className={`px-4 py-2 rounded border ${
              currentPage === 1 || isFetching
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white hover:bg-gray-50"
            }`}
          >
            Previous
          </button>

          <button
            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
            disabled={currentPage === totalPages || isFetching}
            className={`px-4 py-2 rounded border ${
              currentPage === totalPages || isFetching
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white hover:bg-gray-50"
            }`}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default Cancelled;
